import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Button, MediaGallery, Timeline } from '@xxtract/ui';
import { downloadGtinMediaItem } from '@/features/media/media.api';
import { useGtinGalleryItems } from '@/features/media/hooks/useGtinGalleryItems';
import { useOrderTree } from '../hooks/useOrderTree';
import { useGtinTimeline } from '../hooks/useGtinTimeline';
import { buildGtinTimelineItems } from '../gtin-timeline';
import { parseTradeItemId } from '../trade-item-id';
import { GtinLeadtimeBlock } from '../components/GtinLeadtimeBlock';

function DegradedBlock({ title, description, onRetry }: { title: string; description: string; onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-[var(--border)] py-12 text-center" data-testid="degraded-block">
      <span aria-hidden="true" className="material-symbols-outlined text-[32px] text-[var(--warning)]">cloud_off</span>
      <p className="text-sm font-semibold text-[var(--foreground)]">{title}</p>
      <p className="max-w-md text-xs text-[var(--foreground-muted)]">{description}</p>
      <button className="text-xs font-medium text-[var(--secondary)] underline" onClick={onRetry} type="button">{t('common.retry')}</button>
    </div>
  );
}

export function GtinDetailPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { orderNumber, tradeItemId } = useParams<{ orderNumber: string; tradeItemId: string }>();
  const parsed = tradeItemId != null ? parseTradeItemId(tradeItemId) : null;

  const treeQuery = useOrderTree(orderNumber ?? '');
  const timelineQuery = useGtinTimeline(tradeItemId ?? '');
  const galleryQuery = useGtinGalleryItems(parsed ?? { gln: '', gtin: '' }, parsed != null);

  const product = treeQuery.data?.data?.products.find((candidate) => candidate.tradeItemId === tradeItemId) ?? null;
  const timelineEvents = timelineQuery.data?.data ?? null;
  const timelineDegraded = timelineQuery.data?.meta.degraded ?? false;

  function backToOrder() {
    navigate(`/orders/${orderNumber}`);
  }

  const completenessSearchParams = product != null ? `?search=${encodeURIComponent(product.gtin)}` : '';
  const completenessHref = product != null ? `/data-quality/${encodeURIComponent(product.gln)}/products${completenessSearchParams}` : null;

  const galleryLabels = {
    groupLabel: (typeInfo: string) => t(`media.gallery.groups.${typeInfo.toUpperCase()}`, { defaultValue: t('media.gallery.groups.UNKNOWN') }),
    emptyState: t('orders.gtin.media.emptyState'),
    imageAlt: (fileName: string) => t('media.gallery.imageAlt', { fileName }),
    enlarge: (fileName: string) => t('media.gallery.enlarge', { fileName }),
    download: (fileName: string) => t('media.gallery.download', { fileName }),
    dialogTitle: (fileName: string) => t('media.gallery.dialogTitle', { fileName }),
    close: t('common.close'),
    placeholder: t('media.gallery.placeholder'),
  };

  return (
    <div className="space-y-5" data-testid="gtin-detail-page">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('orders.gtin.title', { gtin: parsed?.gtin ?? tradeItemId })}</h1>
          {completenessHref != null && (
            <a className="text-xs font-medium text-[var(--secondary)] underline" data-testid="completeness-link" href={completenessHref}>
              {t('orders.gtin.completenessLink')}
            </a>
          )}
        </div>
        <Button onClick={backToOrder} size="sm" variant="outline">{t('orders.gtin.backToOrder')}</Button>
      </header>

      <section aria-label={t('orders.gtin.timelineSectionLabel')} data-testid="gtin-timeline-section">
        {timelineQuery.isLoading ? (
          <div className="space-y-2" data-testid="gtin-timeline-loading">
            {Array.from({ length: 4 }).map((_, index) => <div className="skeleton h-8 rounded-lg" key={index} />)}
          </div>
        ) : timelineDegraded ? (
          <DegradedBlock description={t('orders.gtin.timelineDegraded.description')} onRetry={() => timelineQuery.refetch()} title={t('orders.gtin.timelineDegraded.title')} />
        ) : timelineEvents == null || (timelineEvents.length === 0 && (product?.services.length ?? 0) === 0) ? (
          <div className="rounded-xl border border-dashed border-[var(--border)] py-12 text-center" data-testid="gtin-timeline-empty">
            <p className="text-sm text-[var(--foreground-muted)]">{t('orders.gtin.timelineEmpty')}</p>
          </div>
        ) : (
          <div className="rounded-xl border border-[var(--border)] p-4">
            <Timeline
              ariaLabel={t('orders.gtin.timelineSectionLabel')}
              data-testid="gtin-timeline"
              items={buildGtinTimelineItems(timelineEvents, product?.services ?? [], i18n.language, t)}
              showAllLabel={t('common.showMore')}
              showLessLabel={t('common.showLess')}
            />
          </div>
        )}
      </section>

      <section aria-label={t('orders.gtin.mediaSectionLabel')} data-testid="gtin-media-section">
        <h2 className="mb-2 text-sm font-semibold text-[var(--foreground)]">{t('orders.gtin.mediaSectionLabel')}</h2>
        {galleryQuery.degraded ? (
          <DegradedBlock description={t('orders.gtin.mediaDegraded.description')} onRetry={galleryQuery.refetch} title={t('orders.gtin.mediaDegraded.title')} />
        ) : (
          <MediaGallery
            data-testid="gtin-media-gallery"
            items={galleryQuery.items}
            labels={galleryLabels}
            onDownload={(item) => void downloadGtinMediaItem(parsed ?? { gln: '', gtin: '' }, item.mediaId, item.fileName)}
          />
        )}
      </section>

      <GtinLeadtimeBlock serviceCodes={product?.services.map((service) => service.serviceCode) ?? []} />
    </div>
  );
}
